// First kind migrated to the generic interactable dispatcher.
//
// Picked as Stage 1's proof-of-life because it's the simplest fully-formed
// fixture interaction in the codebase: hover-driven outline (raycast against
// `lidMeshes`), tracked via `stage.hoveredBoxId`, fires an `onBoxHover`
// callback on transition, and on E either opens the closed box or hot-swaps
// to a different box. No modal-scope claim of its own; suppressed when any
// higher-priority modal is active.
//
// `stage.hoveredBoxId` continues to be the canonical "currently-hovered
// long box" state — the kind's `commitHover` keeps it in sync so external
// readers (currently the legacy E branch for hot-swap, future debug overlays,
// etc.) keep working. Once every kind is migrated, hoveredBoxId can be
// retired in favour of the registry's active-state lookup.

import { registerInteractable } from "../_interactable-registry.js";

registerInteractable({
	id: "longbox-lid",
	precedence: 40,
	// ASK-137 §84 — THIS KIND'S OWN PROMPT AND SUBJECT. `stage.js` used to enumerate every kind by
	// hand, twice (`_updateEHint`'s hover ladder and `_hoveredProduct`), which is exactly what the
	// registry's own header says a registry exists to end. The prompt a shopper reads and the product
	// it names belong where the rest of this kind's behaviour already is.
	hoverHint: '<kbd>E</kbd> Open Box',
	productOf(stage, active) {
		return null;
	},

	// Suppressed when the player's attention is held by anything more
	// specific. Mirrors the legacy `idleScope` computation in
	// `_updateHoverAtPointer`. Note these branches read state that other
	// kinds will eventually set (newPileHover etc. → kinds that aren't
	// migrated yet); for Stage 1 we read it from the stage directly,
	// matching the legacy behaviour.
	suppressedBy(stage) {
		if (stage.boxState === "open" || stage.boxState === "browsing" || stage.boxState === "hero") return true;
		if (stage.activeFixtureHero)  return true;
		if (stage.activeVinylBin)     return true;
		if (stage.activeStackTable)   return true;
		if (stage.hoveredPileComic)   return true;
		if (stage.hoveredBook)        return true;
		if (stage.spinnerRack?.hoveredComic) return true;
		return false;
	},

	getTargets(stage) {
		return stage.boxEntries.size > 0 ? stage.lidMeshes : null;
	},

	onHit(stage, mesh) {
		const fixtureId = mesh?.userData?.fixtureId || null;
		return fixtureId ? { fixtureId } : null;
	},

	commitHover(stage, prev, next) {
		const prevId = prev?.fixtureId ?? null;
		const nextId = next?.fixtureId ?? null;
		if (prevId === nextId) return;
		stage.hoveredBoxId = nextId;
		// F-CU16a — the hint is (hovered box × is it open), evaluated by the stage; never pushed raw.
		if (stage._syncBoxHint) stage._syncBoxHint();
		else {
			const entry = nextId ? stage.boxEntries.get(nextId) : null;
			stage.onBoxHover?.(entry ? { id: nextId, name: entry.fixture.name } : null);
		}
	},

	// E behaviour mirrors the legacy lid branch in stage.js's keydown handler:
	// from idle, hovering a long box → either open its lid (current box, was
	// closed) or close the current box first + activate + open this one.
	// The dispatcher only calls this when active state is non-null, which the
	// suppressedBy + state checks above already guarantee — so this is the
	// "ok, we're definitely going to act" branch.
	interactE(stage, active) {
		// Defensive: the legacy E handler skipped the lid branch entirely when
		// pile/book browse was active. suppressedBy already prevents active
		// state in those cases, but keep the check for parity.
		if (stage.pileState !== "none") return false;
		if (stage.bookState !== "none") return false;
		if (stage.boxState === "open" || stage.boxState === "browsing" || stage.boxState === "hero") return false;

		const id = active.fixtureId;
		if (stage.boxEntries.size > 0 && id !== stage.activeBoxId) {
			stage.forceCloseActiveBox();
			stage.activateBox(id);
			stage.openLid();
		} else if (stage.boxState === "closed") {
			stage.openLid();
		}
		return true;
	},
});
